/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import React, { useState, FC, useEffect, ReactNode } from "react";
import {
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import { PanelContentsContainer } from "./Util";
import { BorderedButton } from "components/Common/BorderedButton";
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowSortDown } from "assets/ArrowSortDown.svg";
import { ReactComponent as ArrowSortUp } from "assets/ArrowSortUp.svg";
import { useTaskDataContext } from "context/TaskDataProvider";

type SortItemName = "date" | "name";
/**
 * Type of sort item
 */
interface SortItem {
  /** Item name */
  name: SortItemName;
  /** Item title */
  title: string;
}

/**
 * Type of sort option
 */
interface SortOption {
  /** Specified sort item */
  item: SortItem;
  /** Specified sort order */
  order: number;
}

/**
 * Type of jobset item.
 *
 * @interface TaskHistoryItem
 * @typedef {TaskHistoryItem}
 */
export interface TaskHistoryItem {
  /**
   * Jobset ID
   *
   * @type {string}
   */
  jobsetId: string;
  /**
   * Jobset Name
   *
   * @type {string}
   */
  jobsetName: string;
  /**
   * Date of jobset created
   *
   * @type {string}
   */
  inputDate: Date;
}

/** Sort order: Ascending */
const ORDER_ASC = 1;
/** Sort order: Descending */
const ORDER_DESC = -1;

interface SelectTaskPanelProps {
  /** Tasks to select */
  taskList: TaskHistoryItem[];
  jobsetId?: string;
  /** Action for "new" button clicked */
  onNewClick: () => void;
  /**
   * Action for task selected
   * @param taskId Selected task ID
   */
  onTaskSelected?: (taskId: string) => void;
}

/**
 * Component of selecting task side panel
 * @param{SelectTaskPanelProps} param0 Properties
 * @return{FC} Component
 */
export const SelectTaskPanel: FC<SelectTaskPanelProps> = ({
  taskList,
  jobsetId,
  onNewClick,
  onTaskSelected,
}) => {
  const { t } = useTranslation();
  const [selectedTaskId, setSelectedTaskId] = useState(jobsetId);
  const { taskData } = useTaskDataContext();

  // Effect for mounted
  useEffect(() => {
    if (taskData?.srcJobSetId) {
      setSelectedTaskId(taskData.srcJobSetId);
    }
  }, [taskData.srcJobSetId]);

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    jobsetId: string
  ) => {
    console.debug(`jobsetId: ${jobsetId}`);
    setSelectedTaskId(jobsetId);
  };

  useEffect(() => {
    if (selectedTaskId !== undefined && onTaskSelected) {
      onTaskSelected(selectedTaskId);
    }
  }, [selectedTaskId, onTaskSelected]);

  const sortItems: SortItem[] = [
    { name: "date", title: t("AWVUI-MSG48") },
    { name: "name", title: t("AWVUI-MSG49") },
  ];
  // Active sort option
  const [sortOption, setSortOption] = useState<SortOption>({
    item: sortItems[0],
    order: ORDER_DESC,
  });

  /**
   * Action for sort option changed
   * @param{SortOption} option Sort option
   */
  const handleSortOptionChanged = (option: SortOption) => {
    setSortOption(option);
  };

  const sorted = [...taskList].sort((a, b) => compareTask(a, b, sortOption));
  return (
    <PanelContentsContainer>
      <Typography variant="h5">{t("AWVUI-MSG106")}</Typography>
      <SortToggle
        items={sortItems}
        onChange={handleSortOptionChanged}
        activeOption={sortOption}
      />
      <List
        component="nav"
        sx={{
          backgroundColor: "white",
          margin: "10px 0px 10px 0px",
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
      >
        {sorted.map((item, index) => (
          <div key={`wrap-${index}`}>
            <ListItemButton
              key={index}
              selected={item.jobsetId === selectedTaskId}
              onClick={(event) => handleListItemClick(event, item.jobsetId)}
            >
              <ListItemText primary={item.jobsetName} />
            </ListItemButton>
            <Divider />
          </div>
        ))}
      </List>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          margin: "0px 0px 0px 50px",
        }}
      >
        <BorderedButton onClick={onNewClick}>{t("AWVUI-MSG50")}</BorderedButton>
      </div>
    </PanelContentsContainer>
  );
};

/**
 * Compare 2 tasks for sort
 * @param{SelectTaskList} a 1st task
 * @param{SelectTaskList} b 2nd task
 * @param{SortOption} option Sort option
 * @return{number} -1: a < b / 1: a > b
 */
const compareTask = (
  a: TaskHistoryItem,
  b: TaskHistoryItem,
  option: SortOption
) => {
  if (option.item.name === "name") {
    return option.order * a.jobsetName.localeCompare(b.jobsetName);
  } else {
    return option.order * (a.inputDate.getTime() - b.inputDate.getTime());
  }
};

// **************************************
// Sub components
// **************************************

interface SortToggleProps {
  /** Sort Items */
  items: SortItem[];
  /** Active sort option */
  activeOption: SortOption;
  /** Action for sort option changed */
  onChange: (option: SortOption) => void;
}

const orderIconMap: { [key: number]: ReactNode } = {
  [ORDER_ASC]: <ArrowSortUp />,
  [ORDER_DESC]: <ArrowSortDown />,
};

const SortToggle: React.FC<SortToggleProps> = ({
  items,
  activeOption,
  onChange,
}) => {
  const theme = useTheme();
  const selectedColor = alpha(theme.palette.primary.main, 0.08);
  const handleButtonClick = (itemName: string) => () => {
    onChange({
      item: items.find((i) => i.name === itemName)!,
      order:
        itemName === activeOption.item.name ? -activeOption.order : ORDER_ASC,
    });
  };

  const isActive = (item: SortItem) => {
    return item.name === activeOption.item.name;
  };

  return (
    <Stack direction="row" gap={1}>
      {items.map((item) => (
        <BorderedButton
          key={item.name}
          onClick={handleButtonClick(item.name)}
          endIcon={isActive(item) && orderIconMap[activeOption.order]}
          variant="outlined"
          size="large"
          sx={{
            backgroundColor: isActive(item) ? selectedColor : undefined,
            color: isActive(item)
              ? theme.palette.getContrastText(selectedColor)
              : undefined,
            "@media (hover: none)": {
              ":hover": {
                backgroundColor: isActive(item) ? selectedColor : undefined,
              },
            },
          }}
        >
          {item.title}
        </BorderedButton>
      ))}
    </Stack>
  );
};
